import { DropdownMenu, DropdownMenuSize } from "react-migration/components/DropdownMenu";
import { memo, useCallback, useMemo } from "react";
import { setSelectedProject, setSelectedProjectAndNavigateToSites } from "react-migration/domains/projects/util";

import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";
import { LineLoading } from "react-migration/domains/dashboard/documents/components/LineLoading";
import { ProjectDropdownList } from "./ProjectDropdownList";
import { ProjectDropdownMobile } from "./ProjectDropdownMobile";
import { ProjectEventName, logEventV2 } from "react-migration/lib/util/logEventV2";
import ROUTES from "src/js/router/routes";
import { Theme } from "react-migration/lib/theme/Theme";
import { Tooltip } from "react-migration/components/Tooltip";
import clsx from "clsx";
import { router } from "src/js/router/react-router";
import { openNewProjectModal } from "src/js/stores/navigation/actions";
import { useActiveNavTab } from "react-migration/layouts/nav/ActiveNavTabContext";
import { useDropdownOpenState } from "./useDropdownOpenState";
import { useEventListener } from "usehooks-ts";
import { useIsMobile } from "react-migration/lib/theme/hooks/useIsMobile";
import { useSelectedProjectQuery } from "./useSelectedProjectQuery";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";

export const ProjectDropdown = () => {
  const { isOpen, setIsOpen } = useDropdownOpenState();
  const { selectedProject, error, loading } = useSelectedProjectQuery({ forceUpdateLastOpenedDate: true });
  const { activeTab } = useActiveNavTab();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const triggerLabel = useMemo(() => {
    if (loading || !selectedProject?.title) return <LineLoading />;
    return selectedProject.title;
  }, [loading, selectedProject?.title]);

  const showWorkspaceTitleTooltip = Boolean(selectedProject?.title && !loading);

  useEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Escape" && isOpen) {
      setIsOpen(false);
    }
  });

  const onCreateNewProject = useCallback(() => {
    setIsOpen(false);
    openNewProjectModal("Workspace dropdown");
  }, [setIsOpen]);

  const onSelectProject = useCallback(
    (projectId: string) => {
      if (activeTab === "workspaces") {
        setSelectedProjectAndNavigateToSites(projectId, ProjectEventName.PROJECT_DROPDOWN_PROJECT_SELECTED);
      } else {
        setSelectedProject(projectId, ProjectEventName.PROJECT_DROPDOWN_PROJECT_SELECTED);
      }
      setIsOpen(false);
    },
    [activeTab, setIsOpen]
  );

  const onViewAllProjects = useCallback(() => {
    setIsOpen(false);
    logEventV2({ name: "Workspace dropdown - view all", properties: {} });
    router.push({ name: ROUTES.WORKSPACES });
  }, [setIsOpen]);

  if (error) {
    return null;
  }

  if (isMobile) {
    return (
      <ProjectDropdownMobile
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        selectedProject={selectedProject}
        loading={loading}
        onCreateNewProject={onCreateNewProject}
        onSelectProject={onSelectProject}
      />
    );
  }

  return (
    <DropdownMenu.Provider
      size={DropdownMenuSize.AUTO}
      offset={0}
      isOpen={isOpen}
      onMenuClosed={() => setIsOpen(false)}
    >
      <DropdownMenu.Trigger
        data-testid="project-dropdown-trigger"
        align={DropdownMenu.TriggerAlignment["BOTTOM_LEFT"]}
        className="p-2!"
        onClick={() => {
          if (!isOpen) {
            logEventV2({ name: "Workspace dropdown - opened", properties: {} });
          }
          setIsOpen(!isOpen);
        }}
        disabled={loading}
      >
        <div
          className={clsx(
            "relative",
            "h-full",
            "w-48",
            "flex",
            "items-center",
            "overflow-hidden",
            "cursor-pointer",
            "grow",
            "gap-x-2",
            "px-1.5",
            "py-px",
            "rounded-lg",
            "bg-white",
            "hover:bg-neutral-100"
          )}
          role="button"
        >
          <div className="flex min-w-0 grow flex-col px-1">
            <div className="font-medium uppercase text-text-base-primary-disabled text-3xs/3.5">
              {t("workspaces.workspace")}
            </div>
            <Tooltip.Wrapper key={String(isOpen)}>
              <Tooltip.Trigger asChild>
                <div className="grow truncate select-none text-text-base-primary text-sm font-medium">
                  {triggerLabel}
                </div>
              </Tooltip.Trigger>
              {showWorkspaceTitleTooltip && (
                <Tooltip.Content align="start" side="bottom" theme={Theme.Light} size="sm">
                  <span className="whitespace-normal wrap-break-word">{selectedProject?.title}</span>
                </Tooltip.Content>
              )}
            </Tooltip.Wrapper>
          </div>
          <div>
            <Icon
              size="md"
              cursor="pointer"
              extraClasses="ml-auto"
              icon={isOpen ? "icon-lt-arrow-up-s-line" : "icon-lt-arrow-down-s-line"}
            />
          </div>
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="rounded-t-none! rounded-b-lg z-99999">
        {isOpen && (
          <ProjectDropdownContent
            onCreateNewProject={onCreateNewProject}
            onSelectProject={onSelectProject}
            onViewAll={onViewAllProjects}
            showViewAll={activeTab !== "workspaces"}
          />
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Provider>
  );
};

interface ProjectDropdownContentProps {
  onCreateNewProject(): void;
  onSelectProject(projectId: string): void;
  onViewAll(): void;
  showViewAll: boolean;
}

const ProjectDropdownContent = memo(
  ({ onCreateNewProject, onSelectProject, onViewAll, showViewAll }: ProjectDropdownContentProps) => (
    <div className="p-4 flex flex-col gap-y-3 w-[476px] select-none">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-text-base-primary">Recent</h1>
        {showViewAll && (
          <Button variant="ghost" size="sm" onClick={onViewAll}>
            View all
            <Icon size="sm" cursor="pointer" icon="icon-lt-arrow-right-line" />
          </Button>
        )}
      </div>
      <ProjectDropdownList onSelectProject={onSelectProject} />
      <div className="border-t-2 border-border-base-divider" />
      <Button variant="ghost" size="md" className="p-1! w-full justify-start" onClick={onCreateNewProject}>
        <Icon
          size="lg"
          cursor="pointer"
          icon="icon-lt-add-line"
          extraClasses="bg-neutral-50 rounded-sm text-text-base-secondary w-10 h-10 flex items-center justify-center"
        />
        New workspace
      </Button>
    </div>
  )
);

ProjectDropdownContent.displayName = "ProjectDropdownContent";
